/**
 * 搜索页面 —— 全局搜索结果展示。
 * 支持按 kind 和模块筛选。
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Search as SearchIcon, Heart } from 'lucide-react';
import { SearchEngine, type SearchScope } from '@/core/searchEngine';
import type { ContentRepository } from '@/core/contentRepository';
import type { EncyclopediaEntry, EntryKind } from '@/core/models';
import { ENTRY_KINDS, entryKindTitle } from '@/core/models';
import { EntryIcon } from '@/components/EntryIcon';
import { useLibraryStore } from '@/store/useLibraryStore';
import { cn } from '@/lib/cn';

interface SearchPageProps {
  repository: ContentRepository;
}

export function SearchPage({ repository }: SearchPageProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') ?? '';
  const [input, setInput] = useState(query);
  const [kindFilter, setKindFilter] = useState<EntryKind | ''>('');
  const inputRef = useRef<HTMLInputElement>(null);
  const recentSearches = useLibraryStore((s) => s.recentSearches);
  const recordSearch = useLibraryStore((s) => s.recordSearch);
  const clearRecentSearches = useLibraryStore((s) => s.clearRecentSearches);

  const engine = useMemo(() => new SearchEngine(repository.entries), [repository]);

  const scope = useMemo<SearchScope>(() => {
    if (!kindFilter) return {};
    return { kind: kindFilter as EntryKind };
  }, [kindFilter]);

  const results = useMemo(
    () => (query ? engine.search(query, scope) : []),
    [engine, query, scope],
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    if (q) {
      recordSearch(q);
      setSearchParams({ q });
    }
  };

  // 聚焦输入框
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (query.trim()) {
      recordSearch(query);
      setInput(query);
    }
  }, [query, recordSearch]);

  return (
    <div className="space-y-4">
      {/* 头部 + 搜索栏 */}
      <div className="flex items-center gap-3">
        <Link to="/" className="rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800">
          <ArrowLeft size={20} />
        </Link>
        <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">搜索</h1>
      </div>

      <form onSubmit={handleSubmit} className="relative">
        <SearchIcon
          size={18}
          className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
        />
        <input
          ref={inputRef}
          type="search"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="名称、别名、说明…"
          className="w-full rounded-2xl border border-slate-200 bg-surface-light py-3 pl-12 pr-4 text-base shadow-sm outline-none transition-colors placeholder:text-slate-400 focus:border-sage focus:ring-2 focus:ring-sage/20 dark:border-slate-700 dark:bg-surface-dark dark:focus:border-sage dark:focus:ring-sage/30"
        />
      </form>

      {/* 类别筛选 */}
      <div className="flex flex-wrap gap-1.5">
        <button
          onClick={() => setKindFilter('')}
          className={cn(
            'rounded-full px-3 py-1 text-xs transition-colors',
            kindFilter === ''
              ? 'bg-sage text-white'
              : 'bg-slate-100 text-slate-500 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700',
          )}
        >
          全部
        </button>
        {ENTRY_KINDS.map((kind) => (
          <button
            key={kind}
            onClick={() => setKindFilter(kindFilter === kind ? '' : kind)}
            className={cn(
              'rounded-full px-3 py-1 text-xs transition-colors',
              kindFilter === kind
                ? 'bg-sage text-white'
                : 'bg-slate-100 text-slate-500 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700',
            )}
          >
            {entryKindTitle[kind]}
          </button>
        ))}
      </div>

      {/* 搜索结果 */}
      {query && (
        <p className="text-xs text-slate-400">
          搜索「{query}」{kindFilter ? `（${entryKindTitle[kindFilter]}）` : ''}
          ，共 {results.length} 条结果
        </p>
      )}

      <div className="space-y-1">
        {results.map((entry) => (
          <SearchResultRow key={entry.id} entry={entry} query={query} />
        ))}
      </div>

      {query && results.length === 0 && (
        <div className="py-20 text-center text-sm text-slate-400">
          未找到匹配「{query}」的条目
        </div>
      )}

      {!query && recentSearches.length > 0 && (
        <section className="rounded-2xl border border-slate-200/60 bg-surface-light p-4 shadow-sm dark:border-slate-700/40 dark:bg-surface-dark">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
              最近搜索
            </h2>
            <button
              onClick={clearRecentSearches}
              className="text-xs text-slate-400 hover:text-red-500"
            >
              清空
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {recentSearches.map((item) => (
              <Link
                key={item}
                to={`/search?q=${encodeURIComponent(item)}`}
                className="rounded-full bg-sage/10 px-3 py-1.5 text-xs font-medium text-sage transition-colors hover:bg-sage/20"
              >
                {item}
              </Link>
            ))}
          </div>
        </section>
      )}

      {!query && recentSearches.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 text-slate-400">
          <SearchIcon size={48} className="mb-4 opacity-50" />
          <p className="text-sm">输入关键词开始搜索</p>
          <p className="mt-1 text-xs">支持中英文名称、别名和说明搜索</p>
        </div>
      )}
    </div>
  );
}

/* ------------------------ SearchResultRow ------------------------- */

interface SearchResultRowProps {
  entry: EncyclopediaEntry;
  query: string;
}

function SearchResultRow({ entry, query }: SearchResultRowProps) {
  const isFavorite = useLibraryStore((s) => s.isFavorite(entry.id));
  const toggleFavorite = useLibraryStore((s) => s.toggleFavorite);
  const recordVisit = useLibraryStore((s) => s.recordVisit);

  const handleClick = () => {
    recordVisit(entry.id);
  };

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleFavorite(entry.id);
  };

  // 高亮搜索词
  const highlight = (text: string) => {
    if (!query.trim()) return text;
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? (
        <span key={i} className="bg-amber/20 font-medium text-amber">
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  return (
    <Link
      to={`/topic/${entry.topicID}#${entry.id}`}
      onClick={handleClick}
      className="flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800"
    >
      <EntryIcon entry={entry} size={36} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">
          {highlight(entry.nameZh)}
          {entry.nameEn && (
            <span className="ml-1.5 text-xs text-slate-400">
              {highlight(entry.nameEn)}
            </span>
          )}
        </p>
        {entry.summary && (
          <p className="truncate text-xs text-slate-400">
            {highlight(entry.summary.slice(0, 80))}
          </p>
        )}
      </div>
      <button
        onClick={handleToggle}
        className={cn(
          'shrink-0 transition-colors',
          isFavorite ? 'text-red-400' : 'text-slate-300 hover:text-red-300',
        )}
      >
        <Heart size={16} fill={isFavorite ? 'currentColor' : 'none'} />
      </button>
    </Link>
  );
}
